import { FC, ReactNode, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Drawer } from 'src/components/widgets/drawer';
import { Icon } from 'src/components/widgets/icon';
import { aiDrawerHeader } from 'src/components/shared/aiAssistant';
import { FullscreenIframeCloseButton } from 'src/components/widgets/fullscreenIframeLayout';
import { useRelayStore } from 'src/relay/environment';
import { T } from 'src/shared/helpers';
import theme from 'src/shared/theme';
import SmartToyIcon from 'src/shared/material_icons/hardware/smart_toy.svg';
import CloseIcon from 'src/shared/material_icons/navigation/close.svg';
import styled from 'styled-components';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { AgentAssistantLauncherGuideQuery } from './__generated__/AgentAssistantLauncherGuideQuery.graphql';
import { AgentAssistant } from './AgentAssistant';
import { useAgentAssistant } from './AgentAssistantContext';
import { AgentGuide } from './types';

const AI_MODE_EXIT_DURATION_MS = 220;

interface AgentAssistantContentProps {
    articleId?: string;
    initialPrompt?: string;
    onExternalChanges: () => void;
    /** Full-screen only: the conversation tools portal into this header slot to save space in the conversation. */
    headerToolsSlot?: HTMLElement | null;
}

const GuideLoading = styled.div`
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    min-width: 0;
    min-height: 0;
    color: #607d8b;
    font-size: 13px;
`;

const AgentAssistantContent: FC<AgentAssistantContentProps> = ({ articleId, initialPrompt, onExternalChanges, headerToolsSlot }) => {
    if (!articleId) return <AgentAssistant initialPrompt={initialPrompt} onExternalChanges={onExternalChanges} headerToolsSlot={headerToolsSlot} />;
    return (
        <Suspense fallback={<GuideLoading>{T._('Loading guide...', 'Admin')}</GuideLoading>}>
            <AgentAssistantGuideContent
                articleId={articleId}
                initialPrompt={initialPrompt}
                onExternalChanges={onExternalChanges}
                headerToolsSlot={headerToolsSlot}
            />
        </Suspense>
    );
};

const AgentAssistantGuideContent: FC<AgentAssistantContentProps & { articleId: string }> = ({
    articleId,
    initialPrompt,
    onExternalChanges,
    headerToolsSlot
}) => {
    const data = useLazyLoadQuery<AgentAssistantLauncherGuideQuery>(
        graphql`
            query AgentAssistantLauncherGuideQuery($id: ID!) {
                zendeskArticle(id: $id) {
                    title
                    body
                }
            }
        `,
        { id: `guid://Zakeke/ZendeskArticle/${articleId}` }
    );
    const guide = useMemo<AgentGuide>(
        () => ({ zendeskArticleId: articleId, title: data.zendeskArticle?.title ?? '', body: data.zendeskArticle?.body ?? '' }),
        [articleId, data.zendeskArticle?.body, data.zendeskArticle?.title]
    );

    return <AgentAssistant initialPrompt={initialPrompt} guide={guide} onExternalChanges={onExternalChanges} headerToolsSlot={headerToolsSlot} />;
};

// Vertically centered in the 64px header: the drawer places its actions 12px from the top.
const CloseAiModeButton = styled(FullscreenIframeCloseButton)`
    margin-top: 2px;
`;

// Same height and colors as the Test Mode top bar (FullscreenIframeTopBar).
const AiModeHeader = styled.div`
    position: relative;
    display: flex;
    min-height: 64px;
    align-items: center;
    gap: 14px;
    padding: 12px 220px 12px 24px;
    background: white;
    color: #1f2937;
`;

const AiModeIdentity = styled.div`
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 12px;
`;

// Centered on the whole header (= center of the page-centered conversation container), not on the space left of the buttons.
// Stacked above the drawer body, which comes later in the DOM: the conversation history dropdown must cover the preview canvas.
const HeaderToolsSlot = styled.div`
    position: absolute;
    z-index: 10;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    align-items: center;
    justify-content: center;
`;

const AiModeIcon = styled.div`
    display: flex;
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    background: ${theme.ai.gradient};
    color: white;

    ${Icon},
    ${Icon} svg {
        width: 22px;
        height: 22px;
    }
`;

// "AI Mode" painted with the AI purple→pink gradient, like the pricing rules AI dialog title.
const AiTitleText = styled.span`
    padding-right: 2px;
    background: ${theme.ai.gradientHorizontal};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
`;

// The title and the early access tag on one line.
const AiTitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 7px;
`;

// A small "early access" tag next to Zakeke AI. A <small> so it escapes the heading's `span` (subtitle) styling.
const EarlyAccessBadge = styled.small`
    padding: 1px 6px;
    border-radius: 999px;
    background: #ede7f6;
    color: #6a1b9a;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.05em;
    line-height: 1.5;
    text-transform: uppercase;
`;

const AiModeHeading = styled.div`
    min-width: 0;

    ${AiTitleText} {
        display: block;
        width: fit-content;
        font-size: 16px;
        font-weight: 600;
        line-height: 1.25;
    }

    span {
        display: block;
        margin-top: 3px;
        overflow: hidden;
        color: #6b7280;
        font-size: 12px;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const AgentSurface = styled.div<{ $aiMode: boolean }>`
    display: flex;
    flex: 1;
    min-width: 0;
    min-height: 0;
    /* AI mode uses the AI Agents Studio page background. */
    background: ${(props) => (props.$aiMode ? 'linear-gradient(160deg, #fdfcff 0%, #faf8ff 50%, #fffafc 100%)' : 'white')};
`;

const AgentWorkspace = styled.div<{ $aiMode: boolean }>`
    display: flex;
    flex: 1;
    width: 100%;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
    /* In AI mode the conversation sits on the page background and the preview is its own card (see AgentAssistant). */
    background: ${(props) => (props.$aiMode ? 'transparent' : 'white')};
    container-type: inline-size;
`;

export interface AgentAssistantLauncherProps {
    /** Starts a new, empty conversation with this message. Restored conversations are never changed. */
    initialPrompt?: string;
    children: ReactNode;
    disabled?: boolean;
}

export const AgentAssistantLauncher: FC<AgentAssistantLauncherProps> = ({ initialPrompt, children, disabled = false }) => {
    const {
        isEnabled,
        isOpen,
        isFullScreen,
        animateFullScreenEntry,
        initialPrompt: requestedPrompt,
        promptRequestKey,
        openClassicMode,
        close,
        changeFullScreen,
        guideRequest,
        sidebarOptions
    } = useAgentAssistant();
    const [isExitingAiMode, setIsExitingAiMode] = useState(false);
    const [headerToolsSlot, setHeaderToolsSlot] = useState<HTMLElement | null>(null);
    const label = T._('Zakeke AI', 'Admin');
    const invalidateRelayStore = useRelayStore((state) => state.invalidateRelayStore);

    const refreshRelayStoreIfNeeded = useCallback(() => {
        const relayStore = useRelayStore.getState();
        if (relayStore.isRelayStoreDirty) relayStore.incrementRelayStoreKey();
    }, []);

    const closeAndRefresh = useCallback(() => {
        refreshRelayStoreIfNeeded();
        close();
    }, [close, refreshRelayStoreIfNeeded]);

    useEffect(() => {
        if (!isExitingAiMode) return;

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const timer = window.setTimeout(
            () => {
                setIsExitingAiMode(false);
                refreshRelayStoreIfNeeded();
                openClassicMode();
            },
            prefersReducedMotion ? 0 : AI_MODE_EXIT_DURATION_MS
        );

        return () => window.clearTimeout(timer);
    }, [isExitingAiMode, openClassicMode, refreshRelayStoreIfNeeded]);

    if ((!isEnabled && !guideRequest) || disabled) return <>{children}</>;

    // A conversation started for a purpose (e.g. creating the product being configured) names it; otherwise the generic caption.
    const sidebarCaption =
        sidebarOptions?.caption ?? (guideRequest ? T._('Learning how to use Zakeke', 'Admin') : T._('Get guided help with your Zakeke tasks.', 'Admin'));

    return (
        <>
            {children}
            {/* The sidebar is the same drawer as the other AI drawers (e.g. the pricing rules one): over the page, which
                it dims, and resizable. Its assistant is mounted only while it is shown, so it never keeps a stale conversation. */}
            <Drawer isOpen={isOpen && !isFullScreen} isResizable zIndex={40} width={620} onClose={closeAndRefresh} header={aiDrawerHeader(sidebarCaption)}>
                {isOpen && !isFullScreen && (
                    <AgentWorkspace $aiMode={false}>
                        <AgentAssistantContent
                            key={promptRequestKey}
                            initialPrompt={requestedPrompt ?? initialPrompt}
                            articleId={guideRequest?.articleId}
                            onExternalChanges={invalidateRelayStore}
                        />
                    </AgentWorkspace>
                )}
            </Drawer>
            <Drawer
                isOpen={isOpen && isFullScreen}
                onClose={closeAndRefresh}
                header={{ title: label, subtitle: sidebarCaption }}
                fullScreenHeader={
                    <AiModeHeader>
                        <AiModeIdentity>
                            <AiModeIcon>
                                <Icon>
                                    <SmartToyIcon />
                                </Icon>
                            </AiModeIcon>
                            <AiModeHeading>
                                <AiTitleRow>
                                    <AiTitleText as='strong'>{label}</AiTitleText>
                                    <EarlyAccessBadge>{T._('early access', 'Admin')}</EarlyAccessBadge>
                                </AiTitleRow>
                                <span>{T._('Your Zakeke back office, driven by AI.', 'Admin')}</span>
                            </AiModeHeading>
                        </AiModeIdentity>
                        <HeaderToolsSlot ref={setHeaderToolsSlot} />
                    </AiModeHeader>
                }
                headerActions={
                    isFullScreen ? (
                        <CloseAiModeButton type='button' aria-label={T._('Close', 'Admin')} disabled={isExitingAiMode} onClick={() => setIsExitingAiMode(true)}>
                            <Icon>
                                <CloseIcon />
                            </Icon>
                        </CloseAiModeButton>
                    ) : undefined
                }
                width={620}
                showFullScreenButton={!isFullScreen}
                showCloseButton={!isFullScreen}
                isFullScreen={isFullScreen}
                onFullScreenChange={changeFullScreen}
                fullScreenTransition='fade'
                animateFullScreenEntry={animateFullScreenEntry}
                isFullScreenExiting={isExitingAiMode}
            >
                {/* Mounted only while AI mode is shown: a hidden instance would keep a stale conversation and show it on the next open. */}
                {({ isFullScreen: fullScreen }) =>
                    isOpen &&
                    fullScreen && (
                        <AgentSurface $aiMode={fullScreen}>
                            <AgentWorkspace $aiMode={fullScreen}>
                                <AgentAssistantContent
                                    key={promptRequestKey}
                                    initialPrompt={requestedPrompt ?? initialPrompt}
                                    articleId={guideRequest?.articleId}
                                    onExternalChanges={invalidateRelayStore}
                                    headerToolsSlot={headerToolsSlot}
                                />
                            </AgentWorkspace>
                        </AgentSurface>
                    )
                }
            </Drawer>
        </>
    );
};
